import { FormArray, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';

import { ErrorCode, ErrorType } from '@constants/error.constant';
import { EmailForm } from '@forms/email-form';
import { SMS_BODY_MAX_LENGTH, SmsForm } from '@forms/sms-form';
import { ContractEmailAttachmentForm } from '@gi/forms/contract-email-attachment-form';
import { FormHelper } from '@helpers/form.helper';
import { ErrorMessage } from '@interfaces/error-message';

export type SendContractFormType = {
  email: EmailForm;
  sms: SmsForm;
  sendByMail: FormControl<boolean>;
  sendBySms: FormControl<boolean>;
  contractAttachments: FormArray<ContractEmailAttachmentForm>;
  mailTemplateId: FormControl<number | undefined>;
};

export class SendContractForm extends FormGroup<SendContractFormType> {
  get email(): EmailForm {
    return this.controls.email;
  }

  get sms(): SmsForm {
    return this.controls.sms;
  }

  get sendByMail(): FormControl<boolean> {
    return this.controls.sendByMail;
  }

  get sendBySms(): FormControl<boolean> {
    return this.controls.sendBySms;
  }

  get contractAttachments(): FormArray<ContractEmailAttachmentForm> {
    return this.controls.contractAttachments;
  }

  get errorMessage(): ErrorMessage | null {
    if (this.valid || !this.touched) {
      return null;
    }

    if (this.hasError('noSendingMethod')) {
      return { message: "Au moins une méthode d'envoi doit être sélectionnée", error: ErrorCode.INVALID };
    }

    return null;
  }

  get mailTemplateId(): FormControl<number | undefined> {
    return this.controls.mailTemplateId;
  }

  constructor() {
    super(
      {
        email: new EmailForm(),
        sms: new SmsForm(),
        sendByMail: new FormControl(true),
        sendBySms: new FormControl(true),
        contractAttachments: new FormArray<ContractEmailAttachmentForm>([]),
        mailTemplateId: new FormControl<number | undefined>(undefined)
      },
      {
        validators: [
          SendContractForm.hasAtLeastOneSendingMethod(),
          SendContractForm.manageSmsValidators(),
          SendContractForm.manageEmailValidators()
        ]
      }
    );
  }

  static hasAtLeastOneSendingMethod(): ValidatorFn {
    return (form: SendContractForm): ValidationErrors | null => {
      const sendByMail = form.sendByMail.value;
      const sendBySms = form.sendBySms.value;

      return sendByMail || sendBySms ? null : { noSendingMethod: true };
    };
  }

  static manageSmsValidators(): ValidatorFn {
    return (form: SendContractForm): ValidationErrors | null => {
      if (form.sendBySms.value) {
        if (!form.sms.body.value) {
          FormHelper.addErrorToControl(form.sms.body, ErrorType.REQUIRED);
        }
        if (form.sms.body.value?.length > SMS_BODY_MAX_LENGTH) {
          FormHelper.addErrorToControl(form.sms.body, ErrorType.MAX_LENGTH, {
            requiredLength: SMS_BODY_MAX_LENGTH,
            actualLength: form.sms.body.value.length
          });
        }
      } else {
        FormHelper.removeErrorFromControl(form.sms.body, ErrorType.MAX_LENGTH);
        FormHelper.removeErrorFromControl(form.sms.body, ErrorType.REQUIRED);
      }
      return null;
    };
  }

  static manageEmailValidators(): ValidatorFn {
    return (form: SendContractForm): ValidationErrors | null => {
      if (form.sendByMail.value) {
        if (!form.email.to.value) {
          FormHelper.addErrorToControl(form.email.to, ErrorType.REQUIRED);
        }
        if (!form.email.from.value) {
          FormHelper.addErrorToControl(form.email.from, ErrorType.REQUIRED);
        }
        if (!form.email.subject.value) {
          FormHelper.addErrorToControl(form.email.subject, ErrorType.REQUIRED);
        }
        if (!form.email.body.value) {
          FormHelper.addErrorToControl(form.email.body, ErrorType.REQUIRED);
        }
      } else {
        FormHelper.removeErrorFromControl(form.email.to, ErrorType.REQUIRED);
        FormHelper.removeErrorFromControl(form.email.from, ErrorType.REQUIRED);
        FormHelper.removeErrorFromControl(form.email.subject, ErrorType.REQUIRED);
        FormHelper.removeErrorFromControl(form.email.body, ErrorType.REQUIRED);
      }

      return null;
    };
  }
}
